import { X } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { FilterState } from "../../types/dashboard";
import {
  MDS_EHR_STATUS_LABELS,
  MDS_FILTER_STATUS_OPTIONS,
} from "../../utils/helper";

interface ActiveFiltersProps {
  filters: FilterState;
  onFilterChange: (field: keyof FilterState, value: string | string[]) => void;
  onClearAllFilters: () => void;
}

export function ActiveFilters({
  filters,
  onFilterChange,
  onClearAllFilters,
}: ActiveFiltersProps) {
  // Lookup by composite value first (e.g. "90,91,92" → "Struck/Deleted"),
  // then by single raw code (e.g. "12" → "Placed in EDS Batch"), then fall
  // back to a bucket-level display name for Care tab / legacy values. This
  // makes active-filter chips match whatever the dropdown showed the user.
  const MDS_COMPOSITE_LABELS: Record<string, string> = Object.fromEntries(
    MDS_FILTER_STATUS_OPTIONS.filter((o) => o.value.includes(",")).map((o) => [
      o.value,
      o.label,
    ]),
  );

  const getStatusDisplayName = (status: string) => {
    if (MDS_COMPOSITE_LABELS[status]) return MDS_COMPOSITE_LABELS[status];
    if (MDS_EHR_STATUS_LABELS[status]) return MDS_EHR_STATUS_LABELS[status];
    const bucketMap: Record<string, string> = {
      QUEUED: "Queued",
      IN_PROGRESS: "In Progress",
      READY_FOR_REVIEW: "Ready For Review",
      PENDING_SUBMISSION: "Pending Submission",
      COMPLETED: "Completed",
      CANCELLED: "Cancelled",
      STRUCK: "Struck",
      ready: "Ready",
      scheduled: "Scheduled",
      assigned: "Assigned",
      in_progress: "In Progress",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return bucketMap[status] ?? status;
  };

  // Remove a specific quick status filter
  const removeQuickFilter = (filterToRemove: string) => {
    const updatedFilters = filters.quickStatusFilters.filter(
      (filter) => filter !== filterToRemove
    );
    onFilterChange("quickStatusFilters", updatedFilters);
  };

  // Remove days open filter
  const removeDaysOpenFilter = () => {
    onFilterChange("daysOpen", "");
  };

  // Remove a single payer filter (NETSOL-12004: multi-select)
  const removePayerFilter = (payerToRemove: string) => {
    const updatedPayers = filters.payer.filter((p) => p !== payerToRemove);
    onFilterChange("payer", updatedPayers);
  };

  // Remove a single assessment type filter (NETSOL-12004: multi-select)
  const removeAssessmentTypeFilter = (typeToRemove: string) => {
    const updatedTypes = filters.assessmentType.filter(
      (t) => t !== typeToRemove
    );
    onFilterChange("assessmentType", updatedTypes);
  };

  // Remove a single station filter
  const removeStationFilter = (stationToRemove: string) => {
    const updatedStations = filters.station.filter(
      (s) => s !== stationToRemove
    );
    onFilterChange("station", updatedStations);
  };

  // Remove assigned owner filter
  const removeAssignedOwnerFilter = () => {
    onFilterChange("assignedOwner", "all");
  };

  // Remove unsigned section filter
  const removeUnsignedSectionFilter = (sectionToRemove: string) => {
    const updatedSections = filters.unsignedSections.filter(
      (section) => section !== sectionToRemove
    );
    onFilterChange("unsignedSections", updatedSections);
  };

  // Check if there are any active filters
  const hasActiveFilters =
    filters.quickStatusFilters.length > 0 ||
    filters.globalSearch.length > 0 ||
    (filters.daysOpen && filters.daysOpen.length > 0) ||
    (filters.payer && filters.payer.length > 0) ||
    (filters.assessmentType && filters.assessmentType.length > 0) ||
    (filters.station && filters.station.length > 0) ||
    (filters.assignedOwner && filters.assignedOwner !== "all") ||
    (filters.unsignedSections && filters.unsignedSections.length > 0);

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
          {/* Quick Status Filter Badges */}
          {filters.quickStatusFilters.map((filter) => (
            <Badge
              key={filter}
              variant="secondary"
              className="h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]"
            >
              <span className="px-1">{getStatusDisplayName(filter)}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeQuickFilter(filter)}
                className="h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {/* Days Open Filter Badge */}
          {filters.daysOpen && (
            <Badge
              variant="secondary"
              className="h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]"
            >
              <span className="px-1">Days Open: {filters.daysOpen}+</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={removeDaysOpenFilter}
                className="h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {/* Payer Filter Badges (NETSOL-12004: multi-select) */}
          {filters.payer && filters.payer.map((payer) => (
            <Badge
              key={payer}
              variant="secondary"
              className="h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]"
            >
              <span className="px-1">Payor: {payer}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removePayerFilter(payer)}
                className="h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {/* Assessment Type Filter Badges (NETSOL-12004: multi-select) */}
          {filters.assessmentType && filters.assessmentType.map((type) => (
            <Badge
              key={type}
              variant="secondary"
              className="h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]"
            >
              <span className="px-1">Type: {type}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeAssessmentTypeFilter(type)}
                className="h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {/* Station Filter Badges */}
          {filters.station && filters.station.map((station) => (
            <Badge
              key={station}
              variant="secondary"
              className="h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]"
            >
              <span className="px-1">Station: {station}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeStationFilter(station)}
                className="h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {/* Assigned Owner Filter Badge */}
          {filters.assignedOwner && filters.assignedOwner !== "all" && (
            <Badge
              variant="secondary"
              className="h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]"
            >
              <span className="px-1">Owner: {filters.assignedOwner}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={removeAssignedOwnerFilter}
                className="h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {/* Unsigned Sections Filter Badges */}
          {filters.unsignedSections && filters.unsignedSections.map((section) => (
            <Badge
              key={section}
              variant="secondary"
              className="h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]"
            >
              <span className="px-1">Section: {section}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeUnsignedSectionFilter(section)}
                className="h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]"
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
      </div>

      {/* Clear All Button */}
      <Button
        variant="ghost"
        onClick={onClearAllFilters}
        className="h-6 px-1 text-[#228189] hover:bg-gray-50 font-bold text-sm shrink-0"
      >
        Clear all
      </Button>
    </div>
  );
}
